// What search engines and link previews are told about a page: its title and description. The
// build writes them for every dataset (scripts/build-seo.ts), the worker puts them in the HTML
// of each page load, and the app sets the same ones when it changes dataset.
import type { TBars, TDatasetMetadata, TItem, TJSONInputItem, TUnit } from '@/types/index.js';
import { toOneLine } from '@/utils/metadata.js';
import { getPrettyMeasures } from '@/utils.js';
import { formatMeasure } from '@/utils/units.js';

export const SITE_URL = 'https://all2scale.com';
export const SITE_NAME = 'All2Scale';

// `image` is the picture link previews show, when the page has one
export type TPageHead = { title: string; description: string; url: string; image?: string };

// The page with no dataset in its address
export const HOME_PAGE: TPageHead = {
	title: `${SITE_NAME}: Size Comparisons at True Scale`,
	description:
		'Compare planets, skyscrapers, anime characters, celebrities and more, side by side at their true relative size.',
	url: `${SITE_URL}/`,
};

export const getDatasetPath = (id: string): string => `/?set=${encodeURIComponent(id)}`;
export const getDatasetUrl = (id: string): string => `${SITE_URL}${getDatasetPath(id)}`;
// Made by scripts/build-share-images.ts, 1200x630
export const getShareImageUrl = (id: string): string => `${SITE_URL}/assets/share/${id}.jpg`;

/**
 * What people would search for, such as "One Piece Height Comparison": the dataset's own
 * `searchTitle`, or else its title as a size comparison.
 */
export const getSearchTitle = ({ title, searchTitle }: TDatasetMetadata): string =>
	searchTitle ?? `${title} Size Comparison`;

export const getPageTitle = (metadata: TDatasetMetadata): string =>
	`${getSearchTitle(metadata)} | ${SITE_NAME}`;

// Read from a dataset file, or loaded in the app, where the sizes left out have been inferred
type TAnyItem = TJSONInputItem | TItem;

// An item's given sizes, or its own `measure` in their place, as its label prints them, and in a
// dataset with `bars`, its bar's value, alone when the dataset hides the sizes.
// A size left out, and inferred from the image on the canvas, is not shown, as on its label.
export const getItemMeasure = (item: TAnyItem, unit?: TUnit, bars?: TBars): string => {
	const sizes =
		(item.measure && toOneLine(item.measure)) ||
		getPrettyMeasures(
			'spriteBox' in item
				? { ...item, unit }
				: {
						width: item.width ?? 0,
						height: item.height ?? 0,
						depth: item.depth ?? 0,
						widthInferred: !item.width,
						heightInferred: !item.height,
						depthInferred: !item.depth,
						unit,
					}
		);
	if (!bars || !item.bar) return sizes;
	return [bars.hideSizes ? '' : sizes, formatMeasure(item.bar, bars.unit)].filter(Boolean).join(', ');
};

/**
 * The dataset's description, then how many items it has and its first and last, which are the
 * smallest and the largest: "… 411 in all, from Mansherry (20 cm) to Zunesha (5 km)."
 */
export const getPageDescription = (metadata: TDatasetMetadata, items: TAnyItem[]): string => {
	const first = items[0];
	const last = items.at(-1);
	const sentences = [
		metadata.description ?? `${metadata.title}, side by side at their true relative size.`,
	];
	if (first && last && items.length > 1) {
		const named = (item: TAnyItem) => {
			const measure = getItemMeasure(item, metadata.unit, metadata.bars);
			return measure ? `${item.name} (${measure})` : item.name;
		};
		sentences.push(`${items.length} in all, from ${named(first)} to ${named(last)}.`);
	}
	return sentences.join(' ');
};

export const getDatasetHead = (id: string, metadata: TDatasetMetadata, items: TAnyItem[]): TPageHead => ({
	title: getPageTitle(metadata),
	description: getPageDescription(metadata, items),
	url: getDatasetUrl(id),
	image: getShareImageUrl(id),
});
